import React, { useState } from "react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

const groupSupersets = (exercises) => {
  const emittedGroups = new Set();

  return exercises.flatMap((exercise) => {
    const group = exercise.is_superset && exercise.superset_group
      ? String(exercise.superset_group)
      : null;

    if (!group) return [exercise];
    if (emittedGroups.has(group)) return [];

    emittedGroups.add(group);
    return exercises.filter(
      (candidate) => candidate.is_superset
        && String(candidate.superset_group) === group,
    );
  });
};

const WorkoutScheduleTable = ({ plan, onPlayVideo, onSaveExerciseLog }) => {
  const [videoModal, setVideoModal] = useState(null);
  const [loadingSlug, setLoadingSlug] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  if (!plan) {
    return (
      <div className="text-center py-5">
        <div style={{ fontSize: 48, marginBottom: 16 }}>🏋️</div>
        <h5 style={{ color: "#333" }}>No workout plan assigned yet</h5>
        <p className="text-muted">Your coach will assign a personalised weekly plan. Check back soon!</p>
      </div>
    );
  }

  // Build max rows = max videos any day has
  const dayData = DAYS.reduce((acc, d) => {
    acc[d] = groupSupersets(plan.days?.[d] || []);
    return acc;
  }, {});

  const maxRows = Math.max(1, ...DAYS.map((d) => dayData[d].length));

  const handleClick = async (video) => {
    if (!video.accessible) return;
    setLoadingSlug(video.slug);
    await onPlayVideo(video, (embedUrl, title) => {
      setVideoModal({ embedUrl, title });
    });
    setLoadingSlug(null);
  };

  const getDraft = (video) => drafts[video.assignment_id] || {
    actual_weight: video.user_log?.actual_weight || "",
    actual_reps: video.user_log?.actual_reps || "",
    comments: video.user_log?.comments || "",
  };

  const updateDraft = (video, field, value) => {
    setDrafts((current) => ({
      ...current,
      [video.assignment_id]: {
        ...getDraft(video),
        [field]: value,
      },
    }));
  };

  const handleSaveLog = async (video) => {
    if (!onSaveExerciseLog || !video.assignment_id) return;

    setSavingId(video.assignment_id);
    setSavedId(null);
    try {
      const log = await onSaveExerciseLog(video, getDraft(video));
      setDrafts((current) => ({
        ...current,
        [video.assignment_id]: {
          actual_weight: log?.actual_weight || "",
          actual_reps: log?.actual_reps || "",
          comments: log?.comments || "",
        },
      }));
      setSavedId(video.assignment_id);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      {/* Plan header */}
      <div className="mb-4">
        <h4 style={{ fontWeight: 800, color: "#111" }}>{plan.name}</h4>
        {plan.notes && <p className="text-muted" style={{ fontSize: 14 }}>{plan.notes}</p>}
      </div>

      {/* Schedule table */}
      <div className="schedule-table table-responsive">
        <table className="table-responsive-md ck-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>#</th>
              {DAYS.map((d) => (
                <th key={d} style={{ minWidth: 130 }}>
                  {DAY_LABELS[d]}
                  {dayData[d].length > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>
                      {dayData[d].length} video{dayData[d].length !== 1 ? "s" : ""}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxRows }).map((_, rowIdx) => (
              <tr key={rowIdx} className={`row_${rowIdx + 1}`}>
                <td className="event-time" style={{ fontWeight: 700, color: "var(--primary)", fontSize: 13 }}>
                  #{rowIdx + 1}
                </td>
                {DAYS.map((day) => {
                  const video = dayData[day][rowIdx];
                  if (!video) return <td key={day} />;

                  const isLocked = !video.accessible;
                  const isLoading = loadingSlug === video.slug;
                  const draft = getDraft(video);
                  const isSaving = savingId === video.assignment_id;
                  const isSaved = savedId === video.assignment_id;
                  const supersetGroup = video.is_superset && video.superset_group
                    ? String(video.superset_group)
                    : null;
                  const previousVideo = dayData[day][rowIdx - 1];
                  const nextVideo = dayData[day][rowIdx + 1];
                  const joinsPrevious = supersetGroup
                    && previousVideo?.is_superset
                    && String(previousVideo.superset_group) === supersetGroup;
                  const joinsNext = supersetGroup
                    && nextVideo?.is_superset
                    && String(nextVideo.superset_group) === supersetGroup;
                  const supersetPosition = !supersetGroup
                    ? ""
                    : joinsPrevious && joinsNext
                      ? "schedule-superset-middle"
                      : joinsPrevious
                        ? "schedule-superset-end"
                        : joinsNext
                          ? "schedule-superset-start"
                          : "schedule-superset-single";

                  return (
                    <td
                      key={day}
                      className={`event ${supersetPosition}`}
                      style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                    >
                      {isLocked ? (
                        <>
                          <span className="title" style={{ color: "#999", cursor: "not-allowed" }}>
                            <i className="fa-solid fa-lock" style={{ marginRight: 5, fontSize: 11 }} />
                            {video.title}
                          </span>
                          <div className="event-tag" style={{ background: "#888" }}>
                            Subscribe to {video.library_name}
                          </div>
                        </>
                      ) : (
                        <>
                          {video.is_superset && (
                            <div className="event-tag schedule-superset-tag">
                              <i className="fa-solid fa-link" aria-hidden="true" />
                              Superset {video.superset_group || ""} · perform together
                            </div>
                          )}
                          <button
                            onClick={() => handleClick(video)}
                            disabled={isLoading}
                            style={{
                              background: "none", border: "none", padding: 0,
                              textAlign: "left", cursor: "pointer", width: "100%",
                            }}
                          >
                            <span className="title" style={{ display: "block" }}>
                              {isLoading
                                ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 5 }} />Loading…</>
                                : <><i className="fa-solid fa-circle-play" style={{ marginRight: 5, color: "var(--primary)" }} />{video.title}</>
                              }
                            </span>
                          </button>
                          <div className="event-tag">{video.library_name}</div>
                          {(video.prescribed_weight || video.prescribed_reps) && (
                            <div className="schedule-prescription">
                              {video.prescribed_weight && <span>Target weight: {video.prescribed_weight}</span>}
                              {video.prescribed_reps && <span>Target reps: {video.prescribed_reps}</span>}
                            </div>
                          )}
                          {video.notes && (
                            <span className="subtitle" style={{ fontSize: 11, color: "#aaa", display: "block", marginTop: 3 }}>
                              {video.notes}
                            </span>
                          )}
                          <div className="schedule-log-form" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              placeholder="Actual weight"
                              value={draft.actual_weight}
                              onChange={(e) => updateDraft(video, "actual_weight", e.target.value)}
                            />
                            <input
                              type="text"
                              placeholder="Actual reps"
                              value={draft.actual_reps}
                              onChange={(e) => updateDraft(video, "actual_reps", e.target.value)}
                            />
                            <textarea
                              placeholder="Comments"
                              value={draft.comments}
                              onChange={(e) => updateDraft(video, "comments", e.target.value)}
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveLog(video)}
                              disabled={isSaving}
                            >
                              {isSaving ? "Saving..." : isSaved ? "Saved" : "Save"}
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="d-flex gap-3 mt-3 flex-wrap" style={{ fontSize: 12, color: "#888" }}>
        <span><i className="fa-solid fa-circle-play" style={{ color: "var(--primary)", marginRight: 4 }} />Click to watch</span>
        <span><i className="fa-solid fa-lock" style={{ marginRight: 4 }} />Requires subscription</span>
        <span><i className="fa-solid fa-link" style={{ color: "var(--primary)", marginRight: 4 }} />Linked exercises are performed as a superset</span>
      </div>

      {/* Video Modal */}
      {videoModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setVideoModal(null)}
        >
          <div
            style={{ width: "92%", maxWidth: 920, aspectRatio: "16/9", position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoModal(null)}
              style={{
                position: "absolute", top: -38, right: 0,
                background: "none", border: "none",
                color: "#fff", fontSize: 26, cursor: "pointer", lineHeight: 1,
              }}
            >
              &times;
            </button>
            <h6 style={{ position: "absolute", top: -36, left: 0, color: "#fff", margin: 0, fontSize: 14 }}>
              {videoModal.title}
            </h6>
            <iframe
              src={videoModal.embedUrl}
              title={videoModal.title}
              style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
};

export default WorkoutScheduleTable;
